import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

export const CourseScheduleAccordionButton = styled.button.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "course-schedule-accordion",
  },
)<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 8px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  font-size: 18px;
  font-weight: 600;
  color: ${blueGreyMui._900};

  .course-schedule__count {
    font-size: 14px;
    font-weight: 400;
    color: ${blueGreyMui._600};
  }

  .course-schedule__badges {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-inline-start: 4px;
  }

  .course-schedule__badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background-color: ${blueGreyMui._100};
    border: 1px solid ${blueGreyMui._300};
    color: ${blueGreyMui._700};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
  }

  .course-schedule__badge--ready {
    background-color: ${greenMui._50};
    border-color: ${greenMui._400};
    color: ${greenMui._800};

    .course-schedule__badge-count {
      color: ${greenMui._700};
    }
  }

  .course-schedule__badge--skipped {
    background-color: ${redMui._50};
    border-color: ${redMui._300};
    color: ${redMui._800};

    .course-schedule__badge-count {
      color: ${redMui._700};
    }
  }

  .course-schedule__badge--total {
    background-color: ${blueGreyMui._50};
    border-color: ${blueGreyMui._300};
    color: ${blueGreyMui._600};

    .course-schedule__badge-count {
      color: ${blueGreyMui._600};
    }
  }

  .course-schedule__badge-count {
    font-weight: 700;
  }

  .course-schedule__chevron {
    margin-left: auto;
    border: solid ${blueGreyMui._600};
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 4px;
    transform: rotate(${props => (props.$isOpen ? "45deg" : "-135deg")});
    transition: transform 0.2s ease;
  }
`;

export const CourseScheduleWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "course-schedule-wrapper",
})`
  margin: 16px 0 24px;
  border: 1px solid ${blueGreyMui._100};
  border-radius: 8px;
  overflow: hidden;

  .course-schedule__filter {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 12px 12px 10px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    color: ${blueGreyMui._800};
    cursor: pointer;
    user-select: none;

    [data-e2e="checkbox-mui-container"] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
      padding: 0;
      flex-shrink: 0;
    }

    > span {
      line-height: 1.25;
      padding-top: 1px;
    }
  }

  .course-schedule__direction {
    font-weight: 600;
  }

  .course-schedule__direction--a {
    color: ${redMui._800};
  }

  .course-schedule__direction--b {
    color: ${blueMui._800};
  }

  .course-schedule__row {
    display: grid;
    grid-template-columns: 145px 80px minmax(0, 1fr) 50px minmax(120px, 1.2fr);
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid ${blueGreyMui._100};
    font-size: 12px;
    font-weight: 500;
    color: ${rydeColors.black};

    &:last-child {
      border-bottom: none;
    }
  }

  .course-schedule__row--skipped {
    color: ${blueGreyMui._400};

    .course-schedule__type-content,
    .course-schedule__name-id,
    .course-schedule__name-id--clickable,
    .course-schedule__name-text,
    .course-schedule__passengers,
    .course-schedule__status-label,
    .course-schedule__ride-id,
    .course-schedule__ride-id--clickable,
    .course-schedule__ride-status,
    .course-schedule__direction,
    .course-schedule__direction--a,
    .course-schedule__direction--b {
      color: ${blueGreyMui._400};
    }

    .course-schedule__name-id--clickable,
    .course-schedule__ride-id--clickable {
      &:hover {
        color: ${blueGreyMui._600};
      }
    }

    .course-schedule__type-content svg path,
    .course-schedule__type-content svg [fill]:not([fill="none"]) {
      fill: ${blueGreyMui._400};
    }
  }

  .course-schedule__row--header {
    padding: 8px 12px;
    background-color: ${blueGreyMui._50};
    font-weight: 700;
    color: ${blueGreyMui._700};
  }

  .course-schedule__name {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-inline-end: 8px;
  }

  .course-schedule__name-id--clickable {
    cursor: pointer;
    color: ${blueGreyMui._900};
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: ${greyMui.A700};
    }

    &:focus-visible {
      outline: 2px solid ${greyMui.A400};
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  .course-schedule__name-text {
    text-decoration: none;
  }

  .course-schedule__passengers {
    text-align: center;
    font-weight: 700;
  }

  .course-schedule__type {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .course-schedule__type-content {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: ${blueGreyMui._700};
  }

  .course-schedule__status {
    display: flex;
    justify-content: flex-end;
  }

  .course-schedule__status-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
  }

  .course-schedule__status-meta {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    min-width: 0;
  }

  .course-schedule__status-label {
    font-size: 11px;
    font-weight: 600;
    color: ${blueGreyMui._700};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }

  .course-schedule__status-cell--canceled .course-schedule__status-label {
    color: ${rydeColors.red};
  }

  .course-schedule__row--skipped
    .course-schedule__status-cell--canceled
    .course-schedule__status-label {
    color: ${rydeColors.red};
  }

  .course-schedule__ride-id-row {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0;
    max-width: 100%;
  }

  .course-schedule__ride-id {
    font-size: 10px;
    font-weight: 600;
    color: ${blueGreyMui._800};
  }

  .course-schedule__ride-status {
    font-size: 10px;
    font-weight: 600;
    color: inherit;
    margin-inline-start: 4px;
  }

  .course-schedule__ride-id--clickable {
    cursor: pointer;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: ${greyMui.A700};
    }

    &:focus-visible {
      outline: 2px solid ${greyMui.A400};
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  .course-schedule__status-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: ${rydeColors.white};
    background-color: ${rydeColors.green};
    flex-shrink: 0;

    &--existing {
      font-size: 14px;
      color: ${rydeColors.white};
      background-color: ${rydeColors.red};
    }

    &--canceled {
      color: ${rydeColors.white};
      background-color: ${rydeColors.red};
    }
  }

  .course-schedule__empty {
    padding: 16px;
    text-align: center;
    font-size: 12px;
    color: ${blueGreyMui._600};
  }

  .course-schedule__row--expandable {
    cursor: pointer;
  }

  .course-schedule__row--booking {
    padding-left: 32px;
    background-color: ${greyMui._100};
    grid-template-columns: 60px 120px minmax(0, 1fr) minmax(0, 1fr);
    font-size: 11px;
    color: ${blueGreyMui._700};
  }

  .course-schedule__expand-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 4px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    width: 16px;
    height: 16px;
  }

  .course-schedule__expand-icon {
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid ${blueGreyMui._600};
    transition: transform 0.2s ease;
    transform: rotate(0deg);

    &--expanded {
      transform: rotate(180deg);
    }
  }

  .course-schedule__booking-id {
    font-weight: 600;
    color: ${blueGreyMui._800};

    &--clickable {
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._900};
      transition: color 0.2s ease;

      &:hover {
        color: ${greyMui.A700};
      }
    }
  }

  .course-schedule__booking-time {
    font-weight: 500;
    color: ${blueGreyMui._700};
  }

  .course-schedule__booking-stations {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  .course-schedule__station {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--clickable {
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._900};
      transition: color 0.2s ease;

      &:hover {
        color: ${greyMui.A700};
      }
    }
  }

  .course-schedule__booking-passenger {
    min-width: 0;
  }

  .course-schedule__row--skipped .course-schedule__name-id--clickable,
  .course-schedule__row--skipped .course-schedule__ride-id--clickable {
    color: ${blueGreyMui._400};

    &:hover {
      color: ${blueGreyMui._600};
    }
  }

  .course-schedule__passenger-text {
    display: block;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    color: ${blueGreyMui._800};
  }

  .course-schedule__passenger-code {
    font-weight: 600;
    color: ${blueGreyMui._800};
    margin-inline-end: 4px;

    &--clickable {
      cursor: pointer;
      text-decoration: underline;
      color: ${blueGreyMui._900};
      transition: color 0.2s ease;

      &:hover {
        color: ${greyMui.A700};
      }
    }
  }
`;

export const CourseScheduleTooltipContent = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "course-schedule-tooltip-content",
  },
)`
  max-width: 300px;
  white-space: normal;
  word-wrap: break-word;
`;
